import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  FileText,
  Briefcase,
  Sparkles,
  Users,
  ChevronDown,
  Plus,
  Loader2,
  X,
} from "lucide-react";
import { useCreateProject } from "../../lib/api/adminProjects";

// Components
import ProjectFormBasicInfo from "../../components/admin/projects/ProjectFormBasicInfo";
import ProjectFormDetails from "../../components/admin/projects/ProjectFormDetails";
import ProjectFormResponsibilities from "../../components/admin/projects/ProjectFormResponsibilities";
import ProjectFormSkills from "../../components/admin/projects/ProjectFormSkills";
import ProjectFormSidebar from "../../components/admin/projects/ProjectFormSidebar";

// Zod Schema
const responsibilitySchema = z.object({
  order: z.number().int().min(1),
  description: z
    .string()
    .min(5, "Responsibility must be at least 5 characters")
    .max(500, "Responsibility cannot exceed 500 characters"),
});

const skillSchema = z.object({
  name: z
    .string()
    .min(2, "Skill name must be at least 2 characters")
    .max(50, "Skill name cannot exceed 50 characters"),
  required: z.boolean().default(true),
});

const projectSchema = z.object({
  title: z
    .string()
    .min(5, "Project title must be at least 5 characters")
    .max(100, "Project title cannot exceed 100 characters"),
  category: z.string().min(1, "Category is required"),
  description: z
    .string()
    .min(20, "Description must be at least 20 characters")
    .max(2000, "Description cannot exceed 2000 characters"),
  hourlyRate: z
    .number()
    .min(1, "Hourly rate must be at least $1")
    .max(500, "Hourly rate cannot exceed $500"),
  estimatedHours: z.string().min(1, "Estimated hours is required"),
  difficulty: z.string().min(1, "Difficulty is required"),
  totalSlots: z
    .number()
    .int("Total slots must be a whole number")
    .min(1, "Total slots must be at least 1")
    .max(1000, "Total slots cannot exceed 1000"),
  responsibilities: z
    .array(responsibilitySchema)
    .min(1, "At least one responsibility is required"),
  skillsRequired: z.array(skillSchema).min(1, "At least one skill is required"),
});

export type ProjectFormData = z.infer<typeof projectSchema>;

const sections = [
  { id: "basic", label: "Basic Info", icon: FileText },
  { id: "details", label: "Details", icon: Briefcase },
  { id: "responsibilities", label: "Responsibilities", icon: Sparkles },
  { id: "skills", label: "Skills", icon: Users },
];

const CreateProject: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const navigate = useNavigate();
  const { mutateAsync: createProject, isPending } = useCreateProject();
  const [activeSection, setActiveSection] = useState<string>("basic");
  const [showMobileNav, setShowMobileNav] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema) as any,
    defaultValues: {
      title: "",
      category: "",
      description: "",
      hourlyRate: undefined,
      estimatedHours: "",
      difficulty: "",
      totalSlots: undefined,
      responsibilities: [{ order: 1, description: "" }],
      skillsRequired: [{ name: "", required: true }],
    },
    mode: "onChange",
  });

  const responsibilitiesFieldArray = useFieldArray({
    control,
    name: "responsibilities",
  });

  const skillsFieldArray = useFieldArray({
    control,
    name: "skillsRequired",
  });

  const watchedValues = watch();

  const onSubmit = async (data: ProjectFormData) => {
    try {
      const formattedData = {
        ...data,
        responsibilities: data.responsibilities.map((r, index) => ({
          ...r,
          order: index + 1,
        })),
      };

      await createProject(formattedData);
      navigate("/admin/projects");
    } catch (error) {
      console.error("Error creating project:", error);
    }
  };

  const toggleSection = (sectionId: string) => {
    setActiveSection(activeSection === sectionId ? "" : sectionId);
    setShowMobileNav(false);
  };

  const handleMobileNavSelect = (sectionId: string) => {
    setActiveSection(sectionId);
    setShowMobileNav(false);
    // Scroll to section
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate("/admin/projects")}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 sm:mb-4 transition-colors text-sm sm:text-base"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden xs:inline">Back to Projects</span>
          <span className="xs:hidden">Back</span>
        </button>

        <div className="flex flex-col gap-2">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            Create New Project
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm lg:text-base">
            <span className="hidden sm:inline">
              Create a project template. You'll assign it to users with
              verification links later.
            </span>
            <span className="sm:hidden">
              Create a new project template for assignments.
            </span>
          </p>
        </div>
      </div>

      {/* Mobile Section Navigation */}
      <div className="lg:hidden mb-4">
        <div className="relative">
          <button
            onClick={() => setShowMobileNav(!showMobileNav)}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 shadow-sm"
          >
            <div className="flex items-center gap-2">
              {(() => {
                const currentSection = sections.find(
                  (s) => s.id === activeSection
                );
                const Icon = currentSection?.icon || FileText;
                return (
                  <>
                    <Icon className="w-4 h-4 text-[#8B52FF]" />
                    <span>{currentSection?.label || "Select Section"}</span>
                  </>
                );
              })()}
            </div>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                showMobileNav ? "rotate-180" : ""
              }`}
            />
          </button>

          {showMobileNav && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMobileNav(false)}
              />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleMobileNavSelect(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                        isActive
                          ? "bg-[#8B52FF]/5 text-[#8B52FF]"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${
                          isActive ? "text-[#8B52FF]" : "text-gray-400"
                        }`}
                      />
                      <span className={isActive ? "font-medium" : ""}>
                        {section.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Mobile Progress Indicator */}
        <div className="flex items-center gap-1 mt-3">
          {sections.map((section, index) => {
            const isCompleted = (() => {
              switch (section.id) {
                case "basic":
                  return !!(
                    watchedValues.title &&
                    watchedValues.category &&
                    watchedValues.description
                  );
                case "details":
                  return !!(
                    watchedValues.hourlyRate &&
                    watchedValues.estimatedHours &&
                    watchedValues.difficulty &&
                    watchedValues.totalSlots
                  );
                case "responsibilities":
                  return (
                    watchedValues.responsibilities?.length > 0 &&
                    watchedValues.responsibilities.every(
                      (r) => r.description?.length >= 5
                    )
                  );
                case "skills":
                  return (
                    watchedValues.skillsRequired?.length > 0 &&
                    watchedValues.skillsRequired.every(
                      (s) => s.name?.length >= 2
                    )
                  );
                default:
                  return false;
              }
            })();

            const isActive = activeSection === section.id;

            return (
              <div
                key={section.id}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  isCompleted
                    ? "bg-emerald-500"
                    : isActive
                      ? "bg-[#8B52FF]"
                      : "bg-gray-200"
                }`}
              />
            );
          })}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <div id="section-basic">
              <ProjectFormBasicInfo
                register={register}
                control={control}
                errors={errors}
                isActive={activeSection === "basic"}
                onToggle={() => toggleSection("basic")}
                descriptionLength={watchedValues.description?.length || 0}
              />
            </div>

            <div id="section-details">
              <ProjectFormDetails
                register={register}
                control={control}
                errors={errors}
                isActive={activeSection === "details"}
                onToggle={() => toggleSection("details")}
              />
            </div>

            <div id="section-responsibilities">
              <ProjectFormResponsibilities
                register={register}
                control={control}
                errors={errors}
                fieldArray={responsibilitiesFieldArray}
                isActive={activeSection === "responsibilities"}
                onToggle={() => toggleSection("responsibilities")}
              />
            </div>

            <div id="section-skills">
              <ProjectFormSkills
                register={register}
                control={control}
                errors={errors}
                fieldArray={skillsFieldArray}
                isActive={activeSection === "skills"}
                onToggle={() => toggleSection("skills")}
              />
            </div>
          </div>

          {/* Sidebar - Hidden on mobile */}
          <div className="hidden lg:block">
            <ProjectFormSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionClick={setActiveSection}
              watchedValues={watchedValues}
              isValid={isValid}
              isPending={isPending}
            />
          </div>
        </div>

        {/* Mobile Fixed Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 p-4 z-30 shadow-lg">
          <div className="flex items-center gap-3 max-w-lg mx-auto">
            <button
              type="button"
              onClick={() => navigate("/admin/projects")}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              <span className="hidden xs:inline">Cancel</span>
            </button>
            <button
              type="submit"
              disabled={isPending || !isValid}
              className="flex-[2] px-4 py-3 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white rounded-xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#8B52FF]/20"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Create Project</span>
                </>
              )}
            </button>
          </div>

          {/* Validation hint */}
          {!isValid && (
            <p className="text-xs text-center text-gray-500 mt-2">
              Complete all required fields to create project
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateProject;
